import React, { useCallback, useRef, useState } from 'react';
import { Box } from '@mui/material';
import type { DetectionLabelMode } from './DetectionLabelToolbar';
import type { OverlayTrack } from '../central-monitoring/CentralMonitoringOverlays';
import { submitDetectionLabel } from '../../services/detectionLabelService';
import {
  mapContainDisplayPointToSource,
  mapRectToContainDisplay,
} from '../../utils/videoOverlayCoords';
import { captureVideoFrameBase64 } from '../../utils/captureVideoFrame';
import { remapPointRotate180 } from '../../utils/cctvDisplayRotate';

type Props = {
  poolId: number;
  cctvId: number;
  mode: DetectionLabelMode;
  tracks: OverlayTrack[];
  containerEl: HTMLDivElement | null;
  videoEl: HTMLVideoElement | null | undefined;
  /** 영상이 CSS 180회전 — 드래그 좌표·프레임 캡처 보정 */
  rotate180?: boolean;
  onSaved?: (message: string) => void;
  onError?: (message: string) => void;
};

/** 오버레이와 동일: 소스(프레임) bbox → video 픽셀 공간 */
function toVideoSpaceBbox(
  bbox: number[],
  frameW: number,
  frameH: number,
  videoW: number,
  videoH: number,
): { x1: number; y1: number; x2: number; y2: number; sw: number; sh: number } {
  let x1 = Number(bbox[0]);
  let y1 = Number(bbox[1]);
  let x2 = Number(bbox[2]);
  let y2 = Number(bbox[3]);
  let sourceW = Math.max(1, frameW);
  let sourceH = Math.max(1, frameH);
  const maxX = Math.max(Math.abs(x1), Math.abs(x2));
  const maxY = Math.max(Math.abs(y1), Math.abs(y2));
  const inSourceSpace =
    maxX <= sourceW * 1.08 + 2 && maxY <= sourceH * 1.08 + 2;
  if (
    inSourceSpace &&
    videoW > 0 &&
    videoH > 0 &&
    (videoW !== sourceW || videoH !== sourceH)
  ) {
    const sx = videoW / sourceW;
    const sy = videoH / sourceH;
    x1 *= sx;
    x2 *= sx;
    y1 *= sy;
    y2 *= sy;
    sourceW = videoW;
    sourceH = videoH;
  }
  return { x1, y1, x2, y2, sw: sourceW, sh: sourceH };
}

const DetectionLabelLayer: React.FC<Props> = ({
  poolId,
  cctvId,
  mode,
  tracks,
  containerEl,
  videoEl,
  rotate180 = false,
  onSaved,
  onError,
}) => {
  const dragStart = useRef<{ x: number; y: number } | null>(null);
  const [dragRect, setDragRect] = useState<{
    x: number;
    y: number;
    w: number;
    h: number;
  } | null>(null);

  const frameBase64 = () =>
    captureVideoFrameBase64(videoEl ?? null, { rotate180 });

  const submit = useCallback(
    async (payload: Parameters<typeof submitDetectionLabel>[0]) => {
      try {
        const frame_image_base64 = frameBase64() ?? undefined;
        await submitDetectionLabel({ ...payload, frame_image_base64 });
        onSaved?.(
          frame_image_base64
            ? '라벨과 프레임이 저장 완료'
            : '라벨이 저장 완료. (영상 프레임 캡처 실패 — 재생 중인지 확인)',
        );
      } catch {
        onError?.('라벨 저장에 실패');
      }
    },
    [onSaved, onError, videoEl],
  );

  if (mode === 'off' || !containerEl) return null;

  const displayW = Math.max(1, containerEl.clientWidth);
  const displayH = Math.max(1, containerEl.clientHeight);
  const videoW = Math.max(1, videoEl?.videoWidth || 1920);
  const videoH = Math.max(1, videoEl?.videoHeight || 1080);

  const handleFpClick = (track: OverlayTrack) => {
    const fw = Math.max(1, track.frame_width || videoW);
    const fh = Math.max(1, track.frame_height || videoH);
    const raw = Array.isArray(track.displayBbox) ? track.displayBbox : [];
    if (raw.length < 4) return;
    // 저장은 프레임(소스) 좌표 유지 — 학습 export와 동일 기준
    const bbox: [number, number, number, number] = [
      Number(raw[0]),
      Number(raw[1]),
      Number(raw[2]),
      Number(raw[3]),
    ];
    void submit({
      pool_id: poolId,
      cctv_id: cctvId,
      label_type: 'false_positive',
      bbox,
      track_id: track.trackId,
      class_name: track.class_name,
      frame_width: fw,
      frame_height: fh,
    });
  };

  const blockOpenCctv = (e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (mode !== 'missed_detection') return;
    blockOpenCctv(e);
    e.currentTarget.setPointerCapture(e.pointerId);
    const rect = containerEl.getBoundingClientRect();
    dragStart.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    setDragRect(null);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (mode !== 'missed_detection' || !dragStart.current) return;
    e.stopPropagation();
    const rect = containerEl.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const x0 = dragStart.current.x;
    const y0 = dragStart.current.y;
    setDragRect({
      x: Math.min(x0, x),
      y: Math.min(y0, y),
      w: Math.abs(x - x0),
      h: Math.abs(y - y0),
    });
  };

  const onPointerUp = (e: React.PointerEvent) => {
    if (mode !== 'missed_detection' || !dragStart.current) return;
    blockOpenCctv(e);
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    const rect = containerEl.getBoundingClientRect();
    const x2 = e.clientX - rect.left;
    const y2 = e.clientY - rect.top;
    const x1 = dragStart.current.x;
    const y1 = dragStart.current.y;
    dragStart.current = null;
    setDragRect(null);
    if (Math.abs(x2 - x1) < 8 || Math.abs(y2 - y1) < 8) return;
    let p1 = mapContainDisplayPointToSource(
      x1,
      y1,
      videoW,
      videoH,
      displayW,
      displayH,
    );
    let p2 = mapContainDisplayPointToSource(
      x2,
      y2,
      videoW,
      videoH,
      displayW,
      displayH,
    );
    if (rotate180) {
      p1 = remapPointRotate180(p1.x, p1.y, videoW, videoH);
      p2 = remapPointRotate180(p2.x, p2.y, videoW, videoH);
    }
    const bbox: [number, number, number, number] = [
      Math.max(0, Math.min(p1.x, p2.x)),
      Math.max(0, Math.min(p1.y, p2.y)),
      Math.min(videoW, Math.max(p1.x, p2.x)),
      Math.min(videoH, Math.max(p1.y, p2.y)),
    ];
    if (bbox[2] - bbox[0] < 4 || bbox[3] - bbox[1] < 4) return;
    void submit({
      pool_id: poolId,
      cctv_id: cctvId,
      label_type: 'missed_detection',
      bbox,
      frame_width: videoW,
      frame_height: videoH,
      note: 'drag',
    });
  };

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 60,
        // 부모(오버레이 래퍼)가 pointerEvents:none 이어도 라벨 조작은 받음
        pointerEvents: 'auto',
      }}
    >
      {mode === 'false_positive' &&
        tracks.map((track) => {
          const raw = Array.isArray(track.displayBbox) ? track.displayBbox : [];
          if (raw.length < 4) return null;
          const fw = Math.max(1, track.frame_width || videoW);
          const fh = Math.max(1, track.frame_height || videoH);
          const { x1, y1, x2, y2, sw, sh } = toVideoSpaceBbox(
            raw,
            fw,
            fh,
            videoW,
            videoH,
          );
          const mapped = mapRectToContainDisplay(
            x1,
            y1,
            x2,
            y2,
            sw,
            sh,
            displayW,
            displayH,
          );
          return (
            <Box
              key={`lbl-fp-${track.trackId}`}
              onClick={(ev) => {
                ev.preventDefault();
                ev.stopPropagation();
                handleFpClick(track);
              }}
              sx={{
                position: 'absolute',
                left: mapped.left,
                top: mapped.top,
                width: Math.max(8, mapped.width),
                height: Math.max(8, mapped.height),
                border: '2px dashed rgba(255, 193, 7, 0.95)',
                backgroundColor: 'rgba(255, 193, 7, 0.12)',
                cursor: 'pointer',
                zIndex: 61,
                pointerEvents: 'auto',
                boxSizing: 'border-box',
              }}
            />
          );
        })}
      {mode === 'missed_detection' && (
        <Box
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onClick={blockOpenCctv}
          onDoubleClick={blockOpenCctv}
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 62,
            cursor: 'crosshair',
            pointerEvents: 'auto',
            touchAction: 'none',
            boxSizing: 'border-box',
            border: '2px dashed rgba(41, 182, 246, 0.55)',
            backgroundColor: 'rgba(41, 182, 246, 0.06)',
          }}
        />
      )}
      {dragRect && (
        <Box
          sx={{
            position: 'absolute',
            left: dragRect.x,
            top: dragRect.y,
            width: dragRect.w,
            height: dragRect.h,
            border: '2px solid #29b6f6',
            backgroundColor: 'rgba(41, 182, 246, 0.15)',
            zIndex: 63,
            pointerEvents: 'none',
          }}
        />
      )}
    </Box>
  );
};

export default DetectionLabelLayer;
